/**
 * Fashion Design Studio Service
 *
 * Integrates with backend ImageGeneration, VideoGeneration, and FashionMultiAgent controllers
 * Session management is client-side using localStorage
 *
 * =========================
 * BACKEND PARAMETER MAPPING
 * =========================
 *
 * The backend has smart routing that handles different parameter names:
 *
 * 1. FLEXIBLE FIELD NAMES (all supported):
 *    - referenceImage OR referenceImageUrl
 *    - controlNetImage OR controlNetImageUrl
 *    - contextStrength, preserveStructure
 *
 * 2. AUTOMATIC ROUTING:
 *    - ControlNet models: referenceImage → auto-routed to controlNetImage
 *    - FLUX Kontext: referenceImage + contextStrength/preserveStructure
 *    - FLUX Krea: text-to-image (no reference needed)
 *    - FLUX Krea Redux: requires referenceImage (auto-fallback to flux-krea if missing)
 *
 * 3. MODEL-SPECIFIC PARAMETERS:
 *    - SDXL ControlNet: controlNetImage → control_image_url (fal.ai)
 *    - FLUX ControlNet: referenceImage → controlnet_conditioning_image_url (fal.ai)
 *    - FLUX Kontext: referenceImage + contextStrength (0.0-1.0) + preserveStructure (bool)
 *
 * Frontend Best Practice: Use model-appropriate field names for clarity:
 * - ControlNet models → controlNetImage
 * - FLUX models → referenceImage
 * - Kontext models → referenceImage + context parameters
 */

import { apiClient as api } from './api';
import type {
  FashionDesignSession,
  FashionDesign,
  GenerateFashionImageRequest,
  GenerateFashionImageResponse,
  ImproveDesignPromptRequest,
  ImproveDesignPromptResponse,
  AnimateDesignRequest,
  AnimateDesignResponse,
  Generate360ViewRequest,
  Generate360ViewResponse,
  CreateDesignCollectionRequest,
  CreateDesignCollectionResponse,
  SaveFashionSessionRequest,
  LoadFashionSessionRequest,
  VirtualTryOnRequest,
  VirtualTryOnResponse,
  ExportFashionCollectionRequest,
  ExportFashionCollectionResponse,
  MoodBoardItem,
  FashionCategory,
  DesignIteration
} from '../models/fashionDesignStudio';

// Backend API endpoints
const IMAGE_BASE_URL = '/api/ImageGeneration';
const VIDEO_BASE_URL = '/api/VideoGeneration';
const FASHION_AGENT_URL = '/api/multi-agent/fashion-prompt';

// LocalStorage key for session persistence
const SESSION_KEY = 'fashion-design-session';

/**
 * Auto-detect provider from model name
 * According to backend API, most models use 'falai' provider
 */
function getProviderFromModel(model: string): string {
  // Gemini models
  if (model.includes('gemini')) {
    return 'gemini';
  }

  // Luma Photon models
  if (model.includes('photon')) {
    return 'luma';
  }

  // Default: all FLUX and SDXL models use FalAI
  return 'falai';
}

export const fashionDesignStudioService = {
  // ===== SESSION MANAGEMENT (Client-Side) =====

  /**
   * Create a new fashion design session (stored in localStorage)
   */
  async createSession(
    projectName: string,
    category?: FashionCategory
  ): Promise<FashionDesignSession> {
    const session: FashionDesignSession = {
      sessionId: `fashion-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      userId: 'current-user', // TODO: Get from auth context
      projectName,
      designPhase: 'concept',
      designs: [],
      settings: {
        // FLUX Model Selection Guide:
        // - flux-krea: Pure text-to-image generation (no reference image)
        // - flux-krea-dev-redux: Image variation (requires reference image)
        // - flux-pro-kontext: Context-aware editing with referenceImage, contextStrength, preserveStructure
        defaultModel: 'flux-krea',
        defaultStyle: 'editorial',
        autoSaveEnabled: true,
        promptImprovementEnabled: true,
        nicheAgentIntegration: true,
        preferredModels: {
          textToImage: 'flux-krea',              // Pure text-to-image (no reference)
          imageVariation: 'flux-krea-dev-redux', // Image variation (with reference)
          sketchToImage: 'flux-kontext',         // FLUX Kontext for sketch-to-fashion (best quality)
          sketchToImageFast: 'flux-general',     // FLUX General with hedsketch control (faster alternative)
          sketchToImageLegacy: 'sdxl-controlnet-canny', // Legacy SDXL (speed fallback)
          contextEditing: 'flux-pro-kontext',    // Context-aware editing (Kontext parameters)
          upscaling: 'aura-sr'
        },
        preferredResolution: '1024x1024',
        generationQuality: 'balanced',
        enableNicheAgents: true,
        enablePromptIteration: true,
        autoImprovePrompts: false
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    if (category) {
      const moodBoardItem: MoodBoardItem = {
        id: `mood-${Date.now()}`,
        type: 'inspiration',
        description: `Category: ${category}`,
        tags: [category]
      };
      session.moodBoard = [moodBoardItem];
    }

    this.saveSession({ session });
    return session;
  },

  /**
   * Load an existing session from localStorage
   */
  async loadSession(request: LoadFashionSessionRequest): Promise<FashionDesignSession | null> {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return null;

    const session = JSON.parse(stored) as FashionDesignSession;

    // Convert date strings back to Date objects
    session.createdAt = new Date(session.createdAt);
    session.updatedAt = new Date(session.updatedAt);
    session.designs = session.designs.map(design => ({
      ...design,
      createdAt: new Date(design.createdAt),
      iterations: design.iterations.map(iter => ({
        ...iter,
        timestamp: new Date(iter.timestamp)
      }))
    }));

    return session.sessionId === request.sessionId ? session : null;
  },

  /**
   * Save session to localStorage
   */
  async saveSession(request: SaveFashionSessionRequest): Promise<void> {
    const updatedSession = {
      ...request.session,
      updatedAt: new Date()
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));
  },

  /**
   * Delete session from localStorage
   */
  async deleteSession(sessionId: string): Promise<void> {
    localStorage.removeItem(SESSION_KEY);
  },

  /**
   * Add a design to the session
   */
  async addDesign(
    session: FashionDesignSession,
    name: string,
    description: string,
    category: FashionCategory
  ): Promise<FashionDesign> {
    const design: FashionDesign = {
      id: `design-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      description,
      category,
      phase: session.designPhase,
      createdAt: new Date(),
      iterations: [],
      tags: [],
      colors: [],
      fabrics: []
    };

    session.designs.push(design);
    await this.saveSession({ session });

    return design;
  },

  /**
   * Get design by ID from session
   */
  async getDesign(session: FashionDesignSession, designId: string): Promise<FashionDesign | null> {
    return session.designs.find(d => d.id === designId) || null;
  },

  // ===== IMAGE GENERATION =====

  /**
   * Generate fashion image using ImageGeneration API
   *
   * Model Selection:
   * - flux-krea: Pure text-to-image (no reference image required)
   * - flux-krea-dev-redux: Image variation (requires reference image, auto-falls back to flux-krea if missing)
   * - flux-pro-kontext: Context-aware editing (requires referenceImage + contextStrength/preserveStructure)
   */
  async generateImage(request: GenerateFashionImageRequest): Promise<GenerateFashionImageResponse> {
    try {
      // Auto-detect provider from model name
      const provider = getProviderFromModel(request.model);

      const response = await api.post<any>(`${IMAGE_BASE_URL}/generate`, {
        prompt: request.prompt,
        model: request.model,  // Send the actual model name
        provider: provider,     // Auto-detected provider
        width: request.parameters.width,
        height: request.parameters.height,
        numImages: request.parameters.numImages,
        seed: request.parameters.seed,
        guidanceScale: request.parameters.guidanceScale,
        negativePrompt: request.parameters.negativePrompt,
        userId: request.userId,  // Pass actual userId from request
        sessionId: request.sessionId
      });

      // Map backend response to frontend format
      // API returns: { images: [...], model: "...", seed: ..., metadata: {...} }
      const images = response.data.images || [];

      return {
        iterationId: `iter-${Date.now()}`,
        images: images.map((img: any) => ({
          url: img.url || img.generatedUrl || img,
          width: img.metadata?.width || request.parameters.width,
          height: img.metadata?.height || request.parameters.height,
          seed: response.data.seed || request.parameters.seed || 0
        })),
        model: response.data.model || request.model,
        generationTime: 0,
        cost: response.data.cost?.amount || 0
      };
    } catch (error: any) {
      console.error('Image generation failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to generate image');
    }
  },

  /**
   * Convert sketch to realistic fashion image
   *
   * Recommended Models (in order of quality):
   * 1. flux-kontext: Best for fashion sketches with context-aware generation
   * 2. flux-general: Fast alternative with hedsketch control type
   * 3. sdxl-controlnet-canny: Legacy fallback for speed
   *
   * FLUX Kontext automatically understands sketch inputs and converts to photorealistic fashion.
   *
   * Backend Smart Routing:
   * - SDXL ControlNet models: referenceImage → auto-routed to controlNetImage
   * - FLUX models: referenceImage → used directly
   * - Backend accepts both "referenceImage" and "controlNetImage" field names
   */
  async sketchToImage(request: GenerateFashionImageRequest): Promise<GenerateFashionImageResponse> {
    try {
      const provider = getProviderFromModel(request.model);

      // Detect model type for parameter optimization
      const isFluxKontext = request.model.includes('kontext');
      const isFluxGeneral = request.model.includes('flux-general');
      const isControlNet = request.model.includes('controlnet');

      const payload: any = {
        prompt: request.prompt,
        model: request.model,
        provider: provider,
        width: request.parameters.width || 1024,
        height: request.parameters.height || 1024,
        numImages: request.parameters.numImages || 1,
        seed: request.parameters.seed,  // Add seed support for reproducibility
        userId: request.userId,  // Pass actual userId from request
        sessionId: request.sessionId
      };

      // Parameter naming: Backend has smart routing that handles both field names
      // For clarity, we use the model-appropriate field name:
      if (isControlNet) {
        // SDXL ControlNet models use controlNetImage (backend maps to control_image_url)
        payload.controlNetImage = request.sketchImage;
      } else {
        // FLUX models use referenceImage
        payload.referenceImage = request.sketchImage;
      }

      // Add FLUX Kontext specific parameters for better sketch interpretation
      if (isFluxKontext) {
        payload.contextStrength = 0.5;  // Lower for creative sketch interpretation
        payload.preserveStructure = false;  // Allow more freedom from sketch
      }

      // Add control type for FLUX General
      if (isFluxGeneral) {
        payload.controlType = 'hedsketch';  // Use hedsketch control for sketch inputs
      }

      const response = await api.post<any>(`${IMAGE_BASE_URL}/generate`, payload);

      // API returns: { images: [...], model: "...", seed: ..., metadata: {...} }
      const images = response.data.images || [];

      return {
        iterationId: `iter-${Date.now()}`,
        images: images.map((img: any) => ({
          url: img.url || img.generatedUrl || img,
          width: img.metadata?.width || request.parameters.width || 1024,
          height: img.metadata?.height || request.parameters.height || 1024,
          seed: response.data.seed || 0
        })),
        model: response.data.model || request.model,
        generationTime: 0,
        cost: response.data.cost?.amount || 0
      };
    } catch (error: any) {
      console.error('Sketch to image failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to convert sketch to image');
    }
  },

  /**
   * Swap clothing using context-aware models (flux-pro-kontext)
   *
   * FLUX Kontext Parameters (backend now supports):
   * - referenceImage: Base64 data URI or URL (required)
   * - contextStrength: 0.0-1.0, controls adherence to reference (default: 0.8)
   * - preserveStructure: true/false, maintains original composition (default: true)
   */
  async swapClothing(request: GenerateFashionImageRequest): Promise<GenerateFashionImageResponse> {
    try {
      const provider = getProviderFromModel(request.model);

      const response = await api.post<any>(`${IMAGE_BASE_URL}/generate`, {
        prompt: request.prompt,
        model: request.model,  // Should be flux-pro-kontext
        provider: provider,
        referenceImage: request.referenceImage,  // Backend accepts both referenceImage and referenceImageUrl
        maskImage: request.maskImage,
        contextStrength: request.parameters.contextStrength || 0.8,  // 0.0-1.0
        preserveStructure: request.parameters.preserveStructure !== false,  // true/false
        width: request.parameters.width || 1024,
        height: request.parameters.height || 1024,
        numImages: request.parameters.numImages || 1,
        seed: request.parameters.seed,  // Add seed support for reproducibility
        userId: request.userId,  // Pass actual userId from request
        sessionId: request.sessionId
      });

      // API returns: { images: [...], model: "...", seed: ..., metadata: {...} }
      const images = response.data.images || [];

      return {
        iterationId: `iter-${Date.now()}`,
        images: images.map((img: any) => ({
          url: img.url || img.generatedUrl || img,
          width: img.metadata?.width || request.parameters.width || 1024,
          height: img.metadata?.height || request.parameters.height || 1024,
          seed: response.data.seed || 0
        })),
        model: response.data.model || request.model,
        generationTime: 0,
        cost: response.data.cost?.amount || 0
      };
    } catch (error: any) {
      console.error('Clothing swap failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to swap clothing');
    }
  },

  /**
   * Place design in different context using context-aware models (flux-pro-kontext)
   *
   * FLUX Kontext Parameters (backend now supports):
   * - referenceImage: Base64 data URI or URL (required)
   * - contextStrength: 0.0-1.0, controls adherence to reference (default: 0.6 for context changes)
   * - preserveStructure: true/false, maintains original composition (default: true)
   */
  async placeInContext(request: GenerateFashionImageRequest): Promise<GenerateFashionImageResponse> {
    try {
      const provider = getProviderFromModel(request.model);

      const response = await api.post<any>(`${IMAGE_BASE_URL}/generate`, {
        prompt: request.prompt,
        model: request.model,  // Should be flux-pro-kontext
        provider: provider,
        referenceImage: request.referenceImage,  // Backend accepts both referenceImage and referenceImageUrl
        contextStrength: request.parameters.contextStrength || 0.6,  // Lower for context changes
        preserveStructure: true,  // Keep original structure when placing in new context
        seed: request.parameters.seed,  // Add seed support for reproducibility
        userId: 'current-user',
        sessionId: request.sessionId
      });

      // API returns: { images: [...], model: "...", seed: ..., metadata: {...} }
      const images = response.data.images || [];

      return {
        iterationId: `iter-${Date.now()}`,
        images: images.map((img: any) => ({
          url: img.url || img.generatedUrl || img,
          width: img.metadata?.width || 1024,
          height: img.metadata?.height || 1024,
          seed: response.data.seed || 0
        })),
        model: response.data.model || 'flux-kontext',
        generationTime: 0,
        cost: response.data.cost?.amount || 0
      };
    } catch (error: any) {
      console.error('Context placement failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to place in context');
    }
  },

  // ===== VIDEO GENERATION =====

  /**
   * Animate a fashion design using VideoGeneration API
   */
  async animateDesign(request: AnimateDesignRequest): Promise<AnimateDesignResponse> {
    try {
      const response = await api.post<any>(`${VIDEO_BASE_URL}/generate`, {
        prompt: `Animate fashion design: ${request.animationType}`,
        imageUrl: request.imageUrl,
        provider: request.model,
        duration: request.duration,
        userId: 'current-user'
      });

      return {
        videoUrl: response.data.generatedUrl || response.data.videoUrl,
        duration: request.duration,
        thumbnailUrl: request.imageUrl
      };
    } catch (error: any) {
      console.error('Animation failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to animate design');
    }
  },

  /**
   * Generate 360-degree view of design
   */
  async generate360View(request: Generate360ViewRequest): Promise<Generate360ViewResponse> {
    try {
      // For 360 views, we generate multiple angles using image generation
      const angles = Array.from({ length: request.numAngles }, (_, i) =>
        (360 / request.numAngles) * i
      );

      const angleImages = await Promise.all(
        angles.map(async (angle) => {
          const response = await api.post<any>(`${IMAGE_BASE_URL}/generate`, {
            prompt: `${request.imageUrl}, rotated ${angle} degrees, same design from different angle`,
            referenceImage: request.imageUrl,
            provider: 'flux-krea',
            userId: 'current-user'
          });

          return {
            angle,
            imageUrl: response.data.generatedUrl || response.data.url || response.data
          };
        })
      );

      let animatedSpinUrl: string | undefined;
      if (request.includeAnimation) {
        const videoResponse = await api.post<any>(`${VIDEO_BASE_URL}/generate`, {
          prompt: '360 degree rotation of fashion design',
          imageUrl: request.imageUrl,
          provider: 'luma',
          duration: 5,
          userId: 'current-user'
        });
        animatedSpinUrl = videoResponse.data.generatedUrl || videoResponse.data.videoUrl;
      }

      return {
        angleImages,
        animatedSpinUrl
      };
    } catch (error: any) {
      console.error('360 view generation failed:', error);
      throw new Error(error.response?.data?.detail || 'Failed to generate 360 view');
    }
  },

  // ===== PROMPT IMPROVEMENT =====

  /**
   * Improve design prompt using FashionMultiAgent
   */
  async improvePrompt(request: ImproveDesignPromptRequest): Promise<ImproveDesignPromptResponse> {
    try {
      const response = await api.post<any>(`${FASHION_AGENT_URL}/start`, {
        initialPrompt: request.prompt,
        category: request.category,
        style: request.style,
        operation: request.operation,
        referenceImages: request.referenceImages || [],
        userId: 'current-user'
      });

      // Extract improved prompt from multi-agent response
      const agentResponse = response.data;

      return {
        originalPrompt: request.prompt,
        improvedPrompt: agentResponse.improvedPrompt || agentResponse.result || request.prompt,
        improvements: agentResponse.improvements || [],
        reasoning: agentResponse.reasoning || 'Improved by fashion design agent',
        suggestedTags: agentResponse.tags || [],
        suggestedColors: agentResponse.colors || [],
        agentUsed: 'FashionMultiAgent'
      };
    } catch (error: any) {
      console.error('Prompt improvement failed:', error);
      // Return original prompt if improvement fails
      return {
        originalPrompt: request.prompt,
        improvedPrompt: request.prompt,
        improvements: [],
        reasoning: 'Prompt improvement unavailable',
        suggestedTags: [],
        suggestedColors: [],
        agentUsed: 'None'
      };
    }
  },

  // ===== COLLECTION MANAGEMENT =====

  /**
   * Create a design collection
   */
  async createCollection(request: CreateDesignCollectionRequest): Promise<CreateDesignCollectionResponse> {
    try {
      // For now, collection creation is client-side
      // In future, could use backend service to generate lookbooks

      return {
        collectionId: `collection-${Date.now()}`,
        collectionImages: []
      };
    } catch (error: any) {
      console.error('Collection creation failed:', error);
      throw new Error('Failed to create collection');
    }
  },

  /**
   * Export fashion collection
   */
  async exportCollection(request: ExportFashionCollectionRequest): Promise<ExportFashionCollectionResponse> {
    try {
      // For now, export is client-side JSON download
      const session = localStorage.getItem(SESSION_KEY);
      if (!session) {
        throw new Error('No session to export');
      }

      const sessionData = JSON.parse(session);
      const dataStr = JSON.stringify(sessionData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      return {
        downloadUrl: url,
        format: request.format,
        fileSize: blob.size
      };
    } catch (error: any) {
      console.error('Export failed:', error);
      throw new Error('Failed to export collection');
    }
  },

  // ===== PROVIDER MANAGEMENT =====

  /**
   * Get available image generation providers
   */
  async getProviders(): Promise<string[]> {
    try {
      const response = await api.get<string[]>(`${IMAGE_BASE_URL}/providers`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to fetch providers:', error);
      // Return default providers if API call fails
      return ['flux-krea', 'flux-kontext', 'flux-dev', 'flux-pro', 'flux-schnell'];
    }
  },

  // ===== VIRTUAL TRY-ON =====

  /**
   * Virtual Try-On: Try garments on models using specialized AI models
   *
   * Supports six models:
   * 1. IDM-VTON: Best quality, returns segmentation mask, requires garment description
   * 2. FASHN V1.6: Commercial-grade (recommended), supports flat-lay photos
   * 3. FASHN V1.5: Previous version with reliable performance
   * 4. CAT-VTON: Versatile with multiple garment type support
   * 5. Leffa: With built-in safety checker and NSFW detection
   * 6. Kling Kolors V1.5: Streamlined with minimal configuration
   *
   * NOTE: This requires backend integration with fal.ai APIs.
   * Backend endpoints needed:
   * - POST /api/ImageGeneration/virtual-tryon/idm-vton
   * - POST /api/ImageGeneration/virtual-tryon/fashn (for V1.5 and V1.6)
   * - POST /api/ImageGeneration/virtual-tryon/cat-vton
   * - POST /api/ImageGeneration/virtual-tryon/leffa
   * - POST /api/ImageGeneration/virtual-tryon/kling-kolors
   *
   * @param request Virtual try-on request with model, human image, garment image
   * @returns Virtual try-on result with generated images
   */
  async virtualTryOn(request: VirtualTryOnRequest): Promise<VirtualTryOnResponse> {
    try {
      const startTime = Date.now();

      // Validate required fields with detailed logging
      console.log('[Virtual Try-On] Request validation:', {
        hasHumanImage: !!request.humanImage,
        humanImageLength: request.humanImage?.length || 0,
        hasGarmentImage: !!request.garmentImage,
        garmentImageLength: request.garmentImage?.length || 0,
        model: request.model,
        sessionId: request.sessionId
      });

      if (!request.humanImage || request.humanImage.trim() === '') {
        throw new Error('Human/model image is required');
      }

      if (!request.garmentImage || request.garmentImage.trim() === '') {
        throw new Error('Garment image is required');
      }

      // Validate model-specific requirements
      if (request.model === 'idm-vton' && !request.garmentDescription) {
        throw new Error('Garment description is required for IDM-VTON model');
      }

      // Build endpoint based on model type - use full model name in URL
      const endpoint = `${IMAGE_BASE_URL}/virtual-tryon/${request.model}`;

      // Validate supported models
      const supportedModels = ['idm-vton', 'fashn-v16', 'fashn-v15', 'cat-vton', 'leffa', 'kling-kolors-v15'];
      if (!supportedModels.includes(request.model)) {
        throw new Error(`Unsupported virtual try-on model: ${request.model}`);
      }

      // Build base payload
      const payload: any = {
        sessionId: request.sessionId,
        userId: request.userId,
        humanImage: request.humanImage,
        garmentImage: request.garmentImage,
        seed: request.seed,
      };

      // Add model-specific parameters
      if (request.model === 'idm-vton') {
        // IDM-VTON specific
        payload.garmentDescription = request.garmentDescription;
        payload.numInferenceSteps = request.numInferenceSteps || 30;
      } else if (request.model === 'fashn-v16' || request.model === 'fashn-v15') {
        // FASHN specific (V1.5 and V1.6 use same parameters)
        // Note: model is determined by URL endpoint, not payload
        payload.category = request.category || 'auto';
        payload.mode = request.mode || 'balanced';
        payload.garmentPhotoType = request.garmentPhotoType || 'auto';
        payload.moderationLevel = request.moderationLevel || 'permissive';
        payload.numSamples = request.numSamples || 1;
        payload.outputFormat = request.outputFormat || 'png';
        payload.segmentationFree = request.segmentationFree !== false; // default true
      } else if (request.model === 'cat-vton') {
        // CAT-VTON specific
        payload.garmentType = request.catGarmentType || 'upper';
        payload.numInferenceSteps = request.numInferenceSteps || 30;
        payload.guidanceScale = request.guidanceScale !== undefined ? request.guidanceScale : 2.5;
      } else if (request.model === 'leffa') {
        // Leffa specific
        payload.garmentType = request.leffaGarmentType || 'upper_body';
        payload.numInferenceSteps = request.numInferenceSteps || 30;
        payload.guidanceScale = request.guidanceScale !== undefined ? request.guidanceScale : 2.5;
        payload.enableSafetyChecker = request.enableSafetyChecker !== false; // default true
        payload.outputFormat = request.outputFormat || 'png';
      }
      // Kling Kolors V1.5 requires no additional parameters beyond base payload

      // Log the complete payload (truncate images for readability)
      const logPayload = {
        ...payload,
        humanImage: payload.humanImage ? `[base64 data: ${payload.humanImage.substring(0, 50)}...]` : 'MISSING',
        garmentImage: payload.garmentImage ? `[base64 data: ${payload.garmentImage.substring(0, 50)}...]` : 'MISSING'
      };
      console.log(`[Virtual Try-On] Calling ${request.model} API:`, endpoint);
      console.log('[Virtual Try-On] Payload:', logPayload);

      const response = await api.post<VirtualTryOnResponse>(endpoint, payload);

      const generationTime = Date.now() - startTime;
      console.log(`[Virtual Try-On] Generation completed in ${generationTime}ms`);

      // Enhance response with metadata
      return {
        ...response.data,
        metadata: {
          ...response.data.metadata,
          generationTime,
          requestedModel: request.model,
        }
      };

    } catch (error: any) {
      console.error('[Virtual Try-On] Failed:', error);

      // Provide helpful error messages
      if (error.response?.status === 404) {
        throw new Error(
          'Virtual Try-On backend endpoint not yet implemented. ' +
          'Backend team needs to add: POST /api/ImageGeneration/virtual-tryon/' + request.model
        );
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Virtual try-on generation failed'
      );
    }
  }
};

export default fashionDesignStudioService;
